import { fa2en, formatPrice } from "~/utils/helpers/inputFormatter"
import { convertDateToUnix, convertUnixToDate, getDateWithAddedDays, getShamsiDate } from "~/utils/helpers/dateTimeFormatter"
import type { IForm, ISection, IField, IHeader } from "~/types/contact/Types";
import { adminNumber } from "~/types/permission/Types";
import { useFileManagerStore } from "~/stores/fileManager";
// import { useI18n } from "vue-i18n";

export function formFormatter(item: IForm): IForm {
  const fields = []
  item.sections.forEach(section => {
    section.fields.forEach(field => {
      fields.push(field)
    });
  });
  return {
    ...item,
    fields: fields,
  };
}

export function submitedDataFormatter(item: { values: any; created_at?: string, ulid: string }): { created_at: string } & typeof item {
  let date = ""
  if (item.created_at) {
    date = getShamsiDate(item.created_at)
  } else {
    const today = getDateWithAddedDays()
    date = getShamsiDate(today)
  }

  const transformedValues = Object.entries(item.values).reduce(
    (acc, [key, value]) => {
      if (typeof value === "object" && "value" in value) {
        acc[key] = Array.isArray(value.value)
          ? value.value.join("")
          : value.value;
      } else {
        acc[key] = Array.isArray(value) ? value.join("") : value;
      }
      return acc;
    },
    {}
  );

  return {
    ulid: item.ulid,
    created_at: date,
    ...transformedValues,
  };
}

export function dataFormatterToSendServer(
  data: { [key: string]: any },
  targetForm: IForm,
  isUpdate: boolean
) {
  const fields: { [key: string]: any } = {};
  targetForm.fields.forEach((field: IField) => {
    let value = data[field.name];
    if (field.name == 'estimated_purchase_price' && value) {
      value = value.replaceAll("٬", "")
    }
    if (field.name in data) {
      switch (field.type) {
        case "single_choice_option_list":
          fields[field.name] = value ? [value] : isUpdate ? null : [];
          break;
        case "numeric_string":
        case "phone":
          value = fa2en(value) || "";
          if (field.type === "phone" && field.name !== "telephone" && value && !value.startsWith("98")) {
            if (/^(09|9)/.test(value)) {
              value = value.replace(/^(09|9)/, "989");
            } else {
              value = value.replace(/^(?:\+98|0)/, "98");
            }
          }
          fields[field.name] = value || (isUpdate ? null : "");
          break;
        case "datetime":
          fields[field.name] = value ? +convertDateToUnix(value) : isUpdate ? null : 0;
          break;
        case "int":
        case "float":
          value = fa2en(value) || 0
          fields[field.name] = value ? +value : isUpdate ? null : 0;
          break;
        default:
          fields[field.name] = value ? value : isUpdate ? null : "";
          break;
      }
    }
    if (field.name == "telephone" && value) {
      fields.telephone = value[0] == 0 ? `98${value.slice(1)}` : value;
    }
  });

  if (!isUpdate) {
    fields.submitter_operator = adminNumber();
  }
  return fields;
}

export function getColumns(
  data: ISection[],
  t: (key: string) => string
): { headers: IHeader[] } {
  const headers: IHeader[] = [];

  // const { t } = useI18n()
  data.forEach((section: ISection) => {
    section.fields.forEach((field) => {
      if (field.main_field) {
        headers.push({
          title: field.title,
          key: field.name,
          main_field_position: field.main_field_position,
        });
      }
    });
  });

  headers.sort((a: IHeader, b: IHeader) =>
    (a.main_field_position ?? 0) - (b.main_field_position ?? 0)
  );
  headers.unshift(
    { title: "#", key: "index" },
    { title: t("Create Date"), key: "created_at" }
  );
  headers.push({ title: t("Actions"), key: "actions" });

  return { headers };
}

export function getSearcheFields(item: IForm, filter: string[]): IField[] {
  const filterData: IField[] = [];
  filter.forEach((element) => {
    const field = item.fields.find((response) => {
      return response.name === element
    });
    if (field) {
      filterData.push(field);
    }
  });
  return filterData;
}

export async function setDataValue(params: any) {
  const fileManagerStore = useFileManagerStore();

  const formData: { [key: string]: any } = {}
  const notes: any[] = []
  const documents: any[] = []
  const attachments: any[] = []
  formData.avatarUrl = ""

  for (let key in params.values) {
    const fieldValue = params.values[key];
    if (fieldValue) {
      if (key == "birth_day") {
        formData[key] = convertUnixToDate(fieldValue)
      } else if (key == 'estimated_purchase_price') {
        formData[key] = formatPrice(fieldValue)
      } else if (key == "whatsApp_phone") {
        formData[key] = fieldValue.startsWith("+") ? fieldValue.substring(1) : fieldValue
      } else if (key == "Avatar") {
        formData[key] = fieldValue
        try {
          formData.avatarUrl = await fileManagerStore.downloadFile({ id: fieldValue }, false);
        } catch (error) {
          console.error("Failed to download avatar:", error);
          formData.avatarUrl = "";
        }
      }
      else if (key !== "notes" && key !== "identity_files" && key !== "attachment_files") {
        formData[key] = typeof fieldValue == "object" ? fieldValue[0] : fieldValue
      }
    }
  }
  if (params.values.notes?.length) {
    params.values.notes.forEach(element => {
      notes.push({
        id: element,
        data: JSON.parse(element)
      })
    });
  }
  if (params.values.identity_files?.length) {
    params.values.identity_files.forEach(element => {
      documents.push({
        id: element,
        data: JSON.parse(element)
      })
    });
  }
  if (params.values.attachment_files?.length) {
    params.values.attachment_files.forEach(element => {
      attachments.push({
        id: element,
        data: JSON.parse(element)
      })
    });
  }
  const targetItem = {
    formData,
    notes,
    documents,
    attachments,
    created_at: getShamsiDate(params.created_at)
  }
  return targetItem
}
